import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useBoolean,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { Navigate } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import StyledButton from '../../../components/StyledButton';
import { pageNames, pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { usePostUserPassword } from '../../../hooks/queries/user';
import useToastHook from '../../../hooks/ToastHook';
import Wireframe from '../../wireframe/Wireframe';

/**
 * パスワード変更画面状態
 */
type PasswordState = {
  /**
   * 新しいパスワード
   */
  passwordNew: string;
  /**
   * 新しいパスワード (確認用)
   */
  passwordNewForCheck: string;
  /**
   * 現在のパスワード
   */
  passwordPresent: string;
};

/**
 * パスワード変更画面
 *
 * - URL: `/password`
 * - Parameter: なし
 * - Query: なし
 *
 * @returns
 */
const Password: React.FunctionComponent = () => {
  const { actions, state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const { control, handleSubmit, setError, formState, reset } =
    useForm<PasswordState>({
      defaultValues: {
        passwordNew: '',
        passwordNewForCheck: '',
        passwordPresent: '',
      },
    });
  const [toShowPasswordNew, { toggle: togglePasswordNew }] = useBoolean();
  const [toShowPasswordNewForCheck, { toggle: togglePasswordNewForCheck }] =
    useBoolean();
  const [toShowPasswordPresent, { toggle: togglePasswordPresent }] =
    useBoolean();
  const changePassword = usePostUserPassword();
  const { toast } = useToastHook();

  const onSubmit = (value: PasswordState) => {
    if (value.passwordNew !== value.passwordNewForCheck) {
      setError('passwordNewForCheck', {
        message: 'パスワードが異なります',
        type: 'custom',
      });
    } else {
      actions.setGuard(true);
      changePassword.mutate(
        {
          currentPassword: value.passwordPresent,
          newPassword: value.passwordNew,
        },
        {
          onSuccess: () => {
            toast('パスワードを更新しました');
            reset();
            actions.setGuard(false);
          },
          onError: (error) => {
            toast(
              error instanceof AppError
                ? error
                : 'パスワードの更新に失敗しました'
            );
            actions.setGuard(false);
          },
        }
      );
    }
  };

  return (
    <Wireframe title={pageNames.PASSWORD}>
      <VStack
        as="form"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        paddingBottom={2}
        width="100%"
      >
        <VStack backgroundColor="white" border="1px" margin={2}>
          <FormControl padding={2}>
            <FormLabel fontWeight="semibold">アカウント名</FormLabel>
            <Text textAlign="left">{state.userInfo?.account}</Text>
          </FormControl>
          <FormControl
            padding={2}
            isRequired
            isInvalid={!TypeUtils.isNil(formState.errors.passwordPresent)}
          >
            <FormLabel htmlFor="passwordPresent" fontWeight="semibold">
              現在のパスワード
            </FormLabel>
            <Controller
              control={control}
              name="passwordPresent"
              render={({ field }) => (
                <InputGroup>
                  <Input
                    autoComplete="new-password"
                    {...field}
                    type={toShowPasswordPresent ? 'text' : 'password'}
                  />
                  <InputRightElement marginRight={1}>
                    <Button size="sm" onClick={togglePasswordPresent}>
                      {toShowPasswordPresent ? (
                        <Icon as={FiEyeOff} />
                      ) : (
                        <Icon as={FiEye} />
                      )}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              )}
            />
            <FormErrorMessage>
              {formState.errors.passwordPresent?.message}
            </FormErrorMessage>
          </FormControl>
          <FormControl
            padding={2}
            isRequired
            isInvalid={!TypeUtils.isNil(formState.errors.passwordNew)}
          >
            <FormLabel htmlFor="passwordNew" fontWeight="semibold">
              新しいパスワード
            </FormLabel>
            <Controller
              control={control}
              name="passwordNew"
              render={({ field }) => (
                <InputGroup>
                  <Input
                    autoComplete="new-password"
                    {...field}
                    type={toShowPasswordNew ? 'text' : 'password'}
                  />
                  <InputRightElement marginRight={1}>
                    <Button size="sm" onClick={togglePasswordNew}>
                      {toShowPasswordNew ? (
                        <Icon as={FiEyeOff} />
                      ) : (
                        <Icon as={FiEye} />
                      )}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              )}
            />
            <FormErrorMessage>
              {formState.errors.passwordNew?.message}
            </FormErrorMessage>
          </FormControl>
          <FormControl
            padding={2}
            isRequired
            isInvalid={!TypeUtils.isNil(formState.errors.passwordNewForCheck)}
          >
            <FormLabel htmlFor="passwordNewForCheck" fontWeight="semibold">
              新しいパスワード (確認用)
            </FormLabel>
            <Controller
              control={control}
              name="passwordNewForCheck"
              render={({ field }) => (
                <InputGroup>
                  <Input
                    autoComplete="nope"
                    {...field}
                    type={toShowPasswordNewForCheck ? 'text' : 'password'}
                  />
                  <InputRightElement marginRight={1}>
                    <Button size="sm" onClick={togglePasswordNewForCheck}>
                      {toShowPasswordNewForCheck ? (
                        <Icon as={FiEyeOff} />
                      ) : (
                        <Icon as={FiEye} />
                      )}
                    </Button>
                  </InputRightElement>
                </InputGroup>
              )}
            />
            <FormErrorMessage>
              {formState.errors.passwordNewForCheck?.message}
            </FormErrorMessage>
          </FormControl>
        </VStack>
        <StyledButton type="submit">変更</StyledButton>
      </VStack>
    </Wireframe>
  );
};

export default Password;
